import { z } from 'zod'
import i18n from '@/i18n/config'

// Tool type enum
export type ToolType = 'mcp' | 'api' | 'internal'

// MCP Auth type
export type MCPAuthType = 'none' | 'token' | 'basic'

export const MCP_AUTH_TYPES: MCPAuthType[] = ['none', 'token', 'basic']

// Factory function to get MCP config schema with current translations
export const getMCPConfigSchema = () =>
  z
    .object({
      server_url: z
        .string()
        .min(1, i18n.t('tools:config.serverUrl'))
        .url(i18n.t('tools:config.serverUrl'))
        .refine(
          (url) => url.startsWith('http://') || url.startsWith('https://'),
          i18n.t('tools:config.serverUrl')
        ),
      auth_type: z.enum(['none', 'token', 'basic']).default('none'),
      api_key: z.string().optional(),
      username: z.string().optional(),
      password: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.auth_type === 'basic') {
        if (!data.username) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('tools:config.usernameRequired'),
            path: ['username'],
          })
        }
        if (!data.password) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('tools:config.passwordRequired'),
            path: ['password'],
          })
        }
      }
    })

// Static schema for backwards compatibility
export const mcpConfigSchema = getMCPConfigSchema()

export type MCPConfig = z.infer<typeof mcpConfigSchema>

// API Configuration
export const apiConfigSchema = z.object({
  base_url: z
    .string()
    .min(1, 'URL base e obrigatoria')
    .url('URL invalida')
    .refine(
      (url) => url.startsWith('http://') || url.startsWith('https://'),
      'URL deve comecar com http:// ou https://'
    ),
  api_key: z.string().optional(),
  auth_header: z.string().default('Authorization'),
  auth_prefix: z.string().default('Bearer'),
})

export type APIConfig = z.infer<typeof apiConfigSchema>

// Tool Configure Request
export interface ToolConfigureRequest {
  config: Record<string, unknown>
  credentials?: Record<string, string>
}

// Tool Configure Response
export interface ToolConfigureResponse {
  id: string
  name: string
  is_configured: boolean
  config_preview: Record<string, unknown>
  message: string
}

// Tool Test Connection Request
export interface ToolTestConnectionRequest {
  config?: Record<string, unknown>
  credentials?: Record<string, string>
}

// Tool Test Connection Response
export interface ToolTestConnectionResponse {
  success: boolean
  message: string
  details?: Record<string, unknown>
}

// MCP Capability
export interface MCPCapability {
  name: string
  description: string | null
  parameters: Record<string, unknown> | null
}

// MCP Capabilities Response
export interface MCPCapabilitiesResponse {
  success: boolean
  server_url: string
  capabilities: MCPCapability[]
  message: string
  server_info?: Record<string, unknown>
}

// Form data types
export interface MCPFormData {
  server_url: string
  auth_type: MCPAuthType
  api_key?: string
  username?: string
  password?: string
}

export interface APIFormData {
  base_url: string
  api_key?: string
  auth_header: string
  auth_prefix: string
}

// Helper to build request from form data
export function buildMCPConfigRequest(data: MCPFormData): ToolConfigureRequest {
  const config: Record<string, unknown> = {
    server_url: data.server_url,
    auth_type: data.auth_type,
  }

  const credentials: Record<string, string> = {}

  if (data.auth_type === 'token' && data.api_key) {
    credentials.api_key = data.api_key
  } else if (data.auth_type === 'basic') {
    if (data.username) credentials.username = data.username
    if (data.password) credentials.password = data.password
  }

  return {
    config,
    credentials: Object.keys(credentials).length > 0 ? credentials : undefined,
  }
}

export function buildAPIConfigRequest(data: APIFormData): ToolConfigureRequest {
  return {
    config: {
      base_url: data.base_url,
      auth_header: data.auth_header,
      auth_prefix: data.auth_prefix,
    },
    credentials: data.api_key ? { api_key: data.api_key } : undefined,
  }
}
